/**
 * @fileoverview Accessible tab panel with optional count badges and controlled selection.
 * @module shared_ui
 *
 * Responsibilities:
 * - Render a horizontal tab bar plus the active tab's content.
 * - Support uncontrolled (defaultTab) and controlled (activeTab/onTabChange) usage.
 * - Implement the WAI-ARIA tabs pattern: role="tablist"/"tab"/"tabpanel", aria-selected,
 *   roving tabindex, and ArrowLeft/ArrowRight/Home/End keyboard navigation.
 *
 * Architecture:
 * - Presentation-only shared element; no data access.
 */
import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

interface Tab {
  id: string;
  label: string;
  content: ReactNode;
  /** Optional count badge rendered after the label (hidden when undefined). */
  badge?: number;
}

interface TabPanelProps {
  tabs: Tab[];
  defaultTab?: string;
  /** Controlled active tab id. When provided, the component does not own selection state. */
  activeTab?: string;
  /** Called whenever the user selects a tab (controlled and uncontrolled modes). */
  onTabChange?: (tabId: string) => void;
}

/**
 * @component TabPanel
 *
 * Purpose:
 * - Shared tabbed-content container used by slide-in detail panels.
 *
 * Props:
 * - tabs: Tab definitions (id, label, content, optional badge count).
 * - defaultTab: Initial tab id for uncontrolled usage.
 * - activeTab: Controlled active tab id (optional).
 * - onTabChange: Selection callback.
 *
 * Behavior:
 * - Arrow keys move focus and selection between tabs; Home/End jump to first/last.
 * - Badges render as small count pills next to the tab label.
 *
 * Data Source:
 * - local (all values passed as props by parent)
 *
 * Constraints:
 * - Presentation-only — content is supplied by the parent.
 */
export default function TabPanel({ tabs, defaultTab, activeTab: controlledTab, onTabChange }: TabPanelProps) {
  const [internalTab, setInternalTab] = useState(defaultTab || tabs[0]?.id || '');
  const activeTab = controlledTab ?? internalTab;
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const selectTab = (tabId: string) => {
    if (controlledTab === undefined) setInternalTab(tabId);
    onTabChange?.(tabId);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = tabs.length - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    const nextTab = tabs[nextIndex];
    if (!nextTab) return;
    selectTab(nextTab.id);
    tabRefs.current[nextTab.id]?.focus();
  };

  const activeContent = tabs.find((t) => t.id === activeTab)?.content;

  return (
    <div className="flex flex-col h-full min-h-0">
      <div role="tablist" aria-label="Detail tabs" className="shrink-0 flex items-center gap-0 px-6 border-b bg-white overflow-x-auto">
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            role="tab"
            id={`tab-${tab.id}`}
            aria-selected={tab.id === activeTab}
            aria-controls={`tabpanel-${tab.id}`}
            tabIndex={tab.id === activeTab ? 0 : -1}
            ref={(el) => { tabRefs.current[tab.id] = el; }}
            onClick={() => selectTab(tab.id)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={`-mb-px min-h-[44px] px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap inline-flex items-center gap-1.5 ${
              tab.id === activeTab
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {tab.label}
            {typeof tab.badge === 'number' && tab.badge > 0 ? (
              <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-orange-500 text-white text-xs font-bold">
                {tab.badge}
              </span>
            ) : null}
          </button>
        ))}
      </div>
      <div
        role="tabpanel"
        id={`tabpanel-${activeTab}`}
        aria-labelledby={`tab-${activeTab}`}
        className="flex-1 min-h-0 overflow-y-auto"
      >
        {activeContent}
      </div>
    </div>
  );
}
